import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import {
  SEARCH_DEBOUNCE_MS,
  useDebouncedCallback,
  usePermissions,
  useSearchPageState,
  type ScopeFilterParams,
} from '@klareospace/pharos-lib-front-utils';
import { ORDER_STATUS, ORDER_STATUS_BUCKET, TENANT_SEGMENT } from '@klareospace/pharos-lib-types';
import type { Order, OrderStatus, SortDirection } from '@klareospace/pharos-lib-types';
import { useMasterContext } from '&/master';

import { useTenantProfile } from '&/features/main-selector';
import { useCarriers } from '&/features/settings/carriers';
import { useDriversForSelect } from '&/features/settings/drivers';
import { useOrderTypes } from '&/features/settings/order-types';
import { useShipperConfigurations } from '&/features/settings/shippers';

import type { SearchOrdersParams } from '../api';
import { useOrders } from './use-orders';
import { UNASSIGNED_DRIVER_VALUE } from '../types/order.types';
import type { OrderSortableColumn, OrderTab, StatusBucketKey } from '../types/order.types';
import { endOfDay, startOfDay } from 'date-fns';
import { MultiSelectOption, type DateRangeValue, usePersistedPageSize } from '@klareospace/pharos-lib-web-utils';

// ─── Constants ────────────────────────────────────────────────────────────────

const ACTION_REQUIRED_STATUSES: OrderStatus[] = [ORDER_STATUS.ANOMALY, ORDER_STATUS.CARRIER_REJECTED];
const DEFAULT_SORT_COLUMN: OrderSortableColumn = 'requestedTimeWindow.start';
const DEFAULT_SORT_DIRECTION = 'asc' as const;
const DEFAULT_STATUS_BUCKETS: StatusBucketKey[] = ['TODO', 'IN_PROGRESS', 'DONE'];
const FILTERS_PAGE_SIZE = 100;
const ORDERS_PAGE_SIZE_STORAGE_KEY = 'orders';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseOrdersPageReturn {
  activeTab: OrderTab;
  canListDrivers: boolean;
  carrierFilter: string[];
  carrierOptions: MultiSelectOption[];
  createDrawerOpen: boolean;
  currentPage: number;
  dateRange: DateRangeValue;
  detailDrawerOpen: boolean;
  driverActiveOptions: MultiSelectOption[];
  driverExtraSelectedOptions: MultiSelectOption[];
  driverFilter: string[];
  driverHiddenCount: number;
  driverInactiveOptions: MultiSelectOption[];
  error: Error | null;
  isDriverOptionsLoading: boolean;
  isDriverSearchActive: boolean;
  isLoading: boolean;
  onCarrierChange: (values: string[]) => void;
  onCloseCreateDrawer: () => void;
  onCloseDetailDrawer: () => void;
  onDateRangeChange: (value: DateRangeValue) => void;
  onDriverChange: (values: string[]) => void;
  onDriverDropdownOpenChange: (open: boolean) => void;
  onDriverSearchChange: (value: string) => void;
  onOpenCreateDrawer: () => void;
  onOpenDetailDrawer: (order: Order) => void;
  onOrderTypeChange: (values: string[]) => void;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onReset: () => void;
  onSearchChange: (value: string) => void;
  onShipperChange: (values: string[]) => void;
  onSort: (sortBy: OrderSortableColumn, sortDirection: SortDirection | null) => void;
  onStatusBucketChange: (values: StatusBucketKey[]) => void;
  onTabChange: (tab: OrderTab) => void;
  orderTypeFilter: string[];
  orderTypeOptions: MultiSelectOption[];
  orders: Order[];
  pageSize: number;
  pageSizeOptions: number[];
  searchValue: string;
  selectedOrder: Order | null;
  shipperFilter: string[];
  shipperOptions: MultiSelectOption[];
  showCarrierFilter: boolean;
  sortBy: OrderSortableColumn | undefined;
  sortDirection: SortDirection | undefined;
  statusBucketFilter: StatusBucketKey[];
  totalItems: number;
  totalPages: number;
}

interface BuildQueryParamsInput {
  activeTab: OrderTab;
  carrierFilter: string[];
  currentPage: number;
  dateRange: DateRangeValue;
  debouncedSearch: string | undefined;
  driverFilter: string[];
  orderTypeFilter: string[];
  pageSize: number;
  shipperFilter: string[];
  siteId: string | null;
  sort: SearchOrdersParams['sort'];
  spaceId: string | null;
  statusBucketFilter: StatusBucketKey[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveStatusesFromBuckets(buckets: StatusBucketKey[]): OrderStatus[] {
  if (buckets.length === 0) return [];
  return buckets.flatMap((bucket) => ORDER_STATUS_BUCKET[bucket]);
}

interface DriverFilterSplit {
  driverIds: string[] | undefined;
  unassignedDriver: true | undefined;
}

function splitDriverFilter(driverFilter: string[]): DriverFilterSplit {
  const driverIds = driverFilter.filter((value) => value !== UNASSIGNED_DRIVER_VALUE);
  const unassignedDriver = driverFilter.includes(UNASSIGNED_DRIVER_VALUE);
  return {
    driverIds: driverIds.length > 0 ? driverIds : undefined,
    unassignedDriver: unassignedDriver || undefined,
  };
}

function buildQueryParams(input: BuildQueryParamsInput): SearchOrdersParams {
  const statuses =
    input.activeTab === 'action_required'
      ? ACTION_REQUIRED_STATUSES
      : resolveStatusesFromBuckets(input.statusBucketFilter);
  const { driverIds, unassignedDriver } = splitDriverFilter(input.driverFilter);
  return {
    carrierIds: input.carrierFilter.length > 0 ? input.carrierFilter : undefined,
    dateFrom: input.dateRange.range.from.toISOString(),
    dateTo: input.dateRange.range.to.toISOString(),
    driverIds,
    orderTypeIds: input.orderTypeFilter.length > 0 ? input.orderTypeFilter : undefined,
    page: input.currentPage,
    pageSize: input.pageSize,
    search: input.debouncedSearch,
    shipperIds: input.shipperFilter.length > 0 ? input.shipperFilter : undefined,
    siteId: input.siteId ?? undefined,
    sort: input.sort ?? { sortBy: DEFAULT_SORT_COLUMN, sortDirection: DEFAULT_SORT_DIRECTION },
    spaceId: input.spaceId ?? undefined,
    statuses: statuses.length > 0 ? statuses : undefined,
    unassignedDriver,
  };
}

function getTodayDateRange(): DateRangeValue {
  const today = new Date();
  return {
    preset: 'today',
    range: { from: startOfDay(today), to: endOfDay(today) },
  };
}

function resolveDriverScopeParams(siteId: string | null, spaceId: string | null): ScopeFilterParams {
  // Backend needs spaceId alongside siteIds, or the carrier-enablement branch is skipped. Both
  // omitted at tenant level (no site/space selected) — a legitimate, unscoped search.
  if (siteId) return { siteIds: [siteId], spaceId: spaceId ?? undefined };
  return { spaceId: spaceId ?? undefined };
}

// ─── Driver filter sub-hook ─────────────────────────────────────────────────────

interface UseDriverFilterOptions {
  canListDrivers: boolean;
  carrierFilter: string[];
  initialDriverFilter: string[];
  initialDriverLabels: Record<string, string>;
  siteId: string | null;
  spaceId: string | null;
}

interface UseDriverFilterReturn {
  driverActiveOptions: MultiSelectOption[];
  driverExtraSelectedOptions: MultiSelectOption[];
  driverFilter: string[];
  driverHiddenCount: number;
  driverInactiveOptions: MultiSelectOption[];
  isDriverOptionsLoading: boolean;
  isDriverSearchActive: boolean;
  onDriverDropdownOpenChange: (open: boolean) => void;
  onDriverSearchChange: (value: string) => void;
  resetDriverFilter: () => void;
  setDriverFilter: (values: string[]) => void;
}

function useDriverFilter({
  canListDrivers,
  carrierFilter,
  initialDriverFilter,
  initialDriverLabels,
  siteId,
  spaceId,
}: UseDriverFilterOptions): UseDriverFilterReturn {
  const [driverFilter, setDriverFilterState] = useState<string[]>(initialDriverFilter);
  const [driverSearchText, setDriverSearchText] = useState('');
  const [hasOpenedDriverDropdown, setHasOpenedDriverDropdown] = useState(false);
  // Not debounced — the pinned "Unassigned" section must disappear as soon as typing starts,
  // otherwise cmdk always counts >=1 item and the empty state never renders.
  const [isDriverSearchActive, setIsDriverSearchActive] = useState(false);
  const driverLabelCacheRef = useRef<Map<string, string>>(new Map(Object.entries(initialDriverLabels)));

  const debouncedSetDriverSearchText = useDebouncedCallback(
    (value: string): void => setDriverSearchText(value),
    SEARCH_DEBOUNCE_MS,
  );

  const driverScopeParams = resolveDriverScopeParams(siteId, spaceId);
  const driverQueriesEnabled = canListDrivers && hasOpenedDriverDropdown;

  const {
    activeOptions: driverActiveOptions,
    hiddenCount: driverHiddenCount,
    inactiveOptions: driverInactiveOptions,
    isLoading: isDriverOptionsLoading,
  } = useDriversForSelect({
    carrierIds: carrierFilter.length > 0 ? carrierFilter : undefined,
    enabled: driverQueriesEnabled,
    siteIds: driverScopeParams.siteIds,
    spaceId: driverScopeParams.spaceId,
    text: driverSearchText || undefined,
  });

  const driverExtraSelectedOptions = useMemo(() => {
    const knownIds = new Set([...driverActiveOptions, ...driverInactiveOptions].map((option) => option.value));
    return driverFilter
      .filter((id) => id !== UNASSIGNED_DRIVER_VALUE && !knownIds.has(id))
      .map((id) => ({ label: driverLabelCacheRef.current.get(id) || id, value: id }));
  }, [driverFilter, driverActiveOptions, driverInactiveOptions]);

  function handleDriverSearchChange(value: string): void {
    setIsDriverSearchActive(value !== '');
    if (value === '') {
      debouncedSetDriverSearchText.cancel();
      setDriverSearchText('');
      return;
    }
    debouncedSetDriverSearchText(value);
  }

  function handleDriverDropdownOpenChange(open: boolean): void {
    if (open) setHasOpenedDriverDropdown(true);
  }

  // Captures selected labels while still visible, so they survive dropping out of view later.
  function setDriverFilter(values: string[]): void {
    const visibleOptions = [...driverActiveOptions, ...driverInactiveOptions];
    for (const value of values) {
      const option = visibleOptions.find((visible) => visible.value === value);
      if (option) driverLabelCacheRef.current.set(option.value, option.label);
    }
    setDriverFilterState(values);
  }

  function resetDriverFilter(): void {
    setDriverFilterState([]);
    debouncedSetDriverSearchText.cancel();
    setDriverSearchText('');
    setIsDriverSearchActive(false);
  }

  return {
    driverActiveOptions,
    driverExtraSelectedOptions,
    driverFilter,
    driverHiddenCount,
    driverInactiveOptions,
    isDriverOptionsLoading,
    isDriverSearchActive,
    onDriverDropdownOpenChange: handleDriverDropdownOpenChange,
    onDriverSearchChange: handleDriverSearchChange,
    resetDriverFilter,
    setDriverFilter,
  };
}

// ─── Create drawer preset sub-hook ──────────────────────────────────────────────

interface UseCreateDrawerPresetOptions {
  hasPermission: (permission: string) => boolean;
  navigate: ReturnType<typeof useNavigate>;
  permissionsLoading: boolean;
  presetCreate: boolean | undefined;
}

interface UseCreateDrawerPresetReturn {
  createDrawerOpen: boolean;
  onCloseCreateDrawer: () => void;
  onOpenCreateDrawer: () => void;
}

function useCreateDrawerPreset({
  hasPermission,
  navigate,
  permissionsLoading,
  presetCreate,
}: UseCreateDrawerPresetOptions): UseCreateDrawerPresetReturn {
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const canCreateOrder = hasPermission('orders:write');

  // One-shot: strip the `create` param from the URL once consumed, so a reload/bookmark doesn't
  // reopen the drawer after the user closes it. Only opens the drawer when the deep-link is backed
  // by the `orders:write` permission — otherwise the param is stripped silently. Waits for the
  // permissions query to settle first: on a cold load `hasPermission` defaults to false while
  // loading, which would otherwise strip the param before it can be honored.
  useEffect(() => {
    if (!presetCreate || permissionsLoading) return;
    if (canCreateOrder) {
      setCreateDrawerOpen(true);
    }
    void navigate({
      replace: true,
      search: (prev: { create?: boolean }) => ({ ...prev, create: undefined }),
      to: '/orders',
    });
  }, [presetCreate, permissionsLoading, canCreateOrder, navigate]);

  function handleOpenCreateDrawer(): void {
    setCreateDrawerOpen(true);
  }

  function handleCloseCreateDrawer(): void {
    setCreateDrawerOpen(false);
  }

  return {
    createDrawerOpen,
    onCloseCreateDrawer: handleCloseCreateDrawer,
    onOpenCreateDrawer: handleOpenCreateDrawer,
  };
}

// ─── Carrier filter sub-hook ─────────────────────────────────────────────────────

interface UseCarrierFilterOptions {
  siteId: string | null;
  spaceId: string | null;
}

interface UseCarrierFilterReturn {
  carrierOptions: MultiSelectOption[];
  showCarrierFilter: boolean;
}

function useCarrierFilter({ siteId, spaceId }: UseCarrierFilterOptions): UseCarrierFilterReturn {
  const { data: tenantProfile } = useTenantProfile();
  const showCarrierFilter =
    tenantProfile !== undefined && tenantProfile.segment !== TENANT_SEGMENT.DELIVERY_SERVICE_PARTNER;

  const { data: carriersData } = useCarriers({
    ...(siteId ? { enabledOnSiteId: siteId } : { spaceId: spaceId ?? undefined }),
    enabled: showCarrierFilter,
    page: 1,
    pageSize: FILTERS_PAGE_SIZE,
  });

  const carrierOptions = useMemo(
    () => (carriersData?.items ?? []).map((carrier) => ({ label: carrier.name, value: carrier.id })),
    [carriersData],
  );

  return { carrierOptions, showCarrierFilter };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useOrdersPage(): UseOrdersPageReturn {
  const { options: pageSizeOptions, pageSize, setPageSize } = usePersistedPageSize(ORDERS_PAGE_SIZE_STORAGE_KEY);
  const searchState = useSearchPageState<OrderSortableColumn>({ pageSize });
  const { siteId, spaceId } = useMasterContext();
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const canListDrivers = hasPermission('drivers:list');
  const navigate = useNavigate();
  const {
    create: presetCreate,
    driverId: presetDriverId,
    driverName: presetDriverName,
  } = useSearch({ from: '/_authenticated/orders/' });
  const initialDriverFilter = useMemo(
    () => (canListDrivers && presetDriverId ? [presetDriverId] : []),
    [canListDrivers, presetDriverId],
  );
  const initialDriverLabels = useMemo(
    () => (canListDrivers && presetDriverId && presetDriverName ? { [presetDriverId]: presetDriverName } : {}),
    [canListDrivers, presetDriverId, presetDriverName],
  );

  // One-shot: strip driverId/driverName from the URL once consumed, so a reload/bookmark doesn't
  // resurrect the preset after the user clears the filter.
  useEffect(() => {
    if (!presetDriverId) return;
    void navigate({
      replace: true,
      search: (prev: { driverId?: string; driverName?: string }) => ({
        ...prev,
        driverId: undefined,
        driverName: undefined,
      }),
      to: '/orders',
    });
  }, [presetDriverId, navigate]);

  const { createDrawerOpen, onCloseCreateDrawer, onOpenCreateDrawer } = useCreateDrawerPreset({
    hasPermission,
    navigate,
    permissionsLoading,
    presetCreate,
  });

  const { carrierOptions, showCarrierFilter } = useCarrierFilter({ siteId, spaceId });

  const { data: orderTypesData } = useOrderTypes({ page: 1, pageSize: FILTERS_PAGE_SIZE });

  const orderTypeOptions = useMemo(
    () => (orderTypesData?.items ?? []).map((orderType) => ({ label: orderType.label, value: orderType.id })),
    [orderTypesData],
  );

  const { data: shipperConfigurationsData } = useShipperConfigurations({ page: 1, pageSize: FILTERS_PAGE_SIZE });

  const shipperOptions = useMemo(() => {
    const seenIds = new Set<string>();
    const options: MultiSelectOption[] = [];
    for (const row of shipperConfigurationsData?.items ?? []) {
      const { id, name } = row.resolvedTemplate;
      if (!seenIds.has(id)) {
        seenIds.add(id);
        options.push({ label: name, value: id });
      }
    }
    return options;
  }, [shipperConfigurationsData]);

  const [activeTab, setActiveTab] = useState<OrderTab>('all');
  const [carrierFilter, setCarrierFilter] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRangeValue>(() => getTodayDateRange());
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [orderTypeFilter, setOrderTypeFilter] = useState<string[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [shipperFilter, setShipperFilter] = useState<string[]>([]);
  const [statusBucketFilter, setStatusBucketFilter] = useState<StatusBucketKey[]>(DEFAULT_STATUS_BUCKETS);
  const savedStatusBucketRef = useRef<StatusBucketKey[]>(DEFAULT_STATUS_BUCKETS);

  const {
    driverActiveOptions,
    driverExtraSelectedOptions,
    driverFilter,
    driverHiddenCount,
    driverInactiveOptions,
    isDriverOptionsLoading,
    isDriverSearchActive,
    onDriverDropdownOpenChange,
    onDriverSearchChange,
    resetDriverFilter,
    setDriverFilter,
  } = useDriverFilter({
    canListDrivers,
    carrierFilter,
    initialDriverFilter,
    initialDriverLabels,
    siteId,
    spaceId,
  });

  const queryParams = buildQueryParams({
    activeTab,
    carrierFilter,
    currentPage: searchState.currentPage,
    dateRange,
    debouncedSearch: searchState.debouncedSearch,
    driverFilter,
    orderTypeFilter,
    pageSize: searchState.pageSize,
    shipperFilter,
    siteId,
    sort: searchState.sort,
    spaceId,
    statusBucketFilter,
  });

  const { data, error, isLoading } = useOrders(queryParams);

  function handleTabChange(tab: OrderTab): void {
    if (tab === 'action_required') {
      savedStatusBucketRef.current = statusBucketFilter;
    } else {
      setStatusBucketFilter(savedStatusBucketRef.current);
    }
    searchState.resetPage();
    setActiveTab(tab);
  }

  function handleStatusBucketChange(values: StatusBucketKey[]): void {
    setStatusBucketFilter(values);
    searchState.resetPage();
  }

  function handleCarrierChange(values: string[]): void {
    setCarrierFilter(values);
    searchState.resetPage();
  }

  function handleShipperChange(values: string[]): void {
    setShipperFilter(values);
    searchState.resetPage();
  }

  function handleOrderTypeChange(values: string[]): void {
    setOrderTypeFilter(values);
    searchState.resetPage();
  }

  function handleDriverChange(values: string[]): void {
    setDriverFilter(values);
    searchState.resetPage();
  }

  const handleOpenDetailDrawer = useCallback((order: Order): void => {
    setSelectedOrder(order);
    setDetailDrawerOpen(true);
  }, []);

  function handleCloseDetailDrawer(): void {
    setDetailDrawerOpen(false);
    setSelectedOrder(null);
  }

  function handleDateRangeChange(value: DateRangeValue): void {
    setDateRange(value);
    searchState.resetPage();
  }

  function handleReset(): void {
    if (activeTab === 'all') {
      setStatusBucketFilter(DEFAULT_STATUS_BUCKETS);
      savedStatusBucketRef.current = DEFAULT_STATUS_BUCKETS;
    }
    setCarrierFilter([]);
    setDateRange(getTodayDateRange());
    resetDriverFilter();
    setOrderTypeFilter([]);
    setShipperFilter([]);
    searchState.onSearchChange('');
    searchState.resetPage();
  }

  return {
    activeTab,
    canListDrivers,
    carrierFilter,
    carrierOptions,
    createDrawerOpen,
    currentPage: searchState.currentPage,
    dateRange,
    detailDrawerOpen,
    driverActiveOptions,
    driverExtraSelectedOptions,
    driverFilter,
    driverHiddenCount,
    driverInactiveOptions,
    error: error ?? null,
    isDriverOptionsLoading,
    isDriverSearchActive,
    isLoading,
    onCarrierChange: handleCarrierChange,
    onCloseCreateDrawer,
    onCloseDetailDrawer: handleCloseDetailDrawer,
    onDateRangeChange: handleDateRangeChange,
    onDriverChange: handleDriverChange,
    onDriverDropdownOpenChange,
    onDriverSearchChange,
    onOpenCreateDrawer,
    onOpenDetailDrawer: handleOpenDetailDrawer,
    onOrderTypeChange: handleOrderTypeChange,
    onPageChange: searchState.onPageChange,
    onPageSizeChange: setPageSize,
    onReset: handleReset,
    onSearchChange: searchState.onSearchChange,
    onShipperChange: handleShipperChange,
    onSort: searchState.onSort,
    onStatusBucketChange: handleStatusBucketChange,
    onTabChange: handleTabChange,
    orderTypeFilter,
    orderTypeOptions,
    orders: data?.items ?? [],
    pageSize: searchState.pageSize,
    pageSizeOptions,
    searchValue: searchState.searchValue,
    selectedOrder,
    shipperFilter,
    shipperOptions,
    showCarrierFilter,
    sortBy: searchState.sortBy,
    sortDirection: searchState.sortDirection,
    statusBucketFilter,
    totalItems: data?.total ?? 0,
    totalPages: data?.totalPages ?? 1,
  };
}
